import { useState } from 'react'
import { Alert, Form, Modal, Typography } from 'antd'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { assignSubscriberVisp, type Subscriber } from './subscriberApi'
import { VispSelect } from './VispSelect'

export function ChangeVispModal({
  subscriber,
  open,
  onClose,
}: {
  subscriber: Subscriber
  open: boolean
  onClose: () => void
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [vispId, setVispId] = useState<string | undefined>()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    if (!vispId) return
    setSaving(true)
    setError(null)
    try {
      await assignSubscriberVisp(subscriber.id, vispId)
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscribers.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.network.all })
      onClose()
    } catch (err) {
      setError(translateError(err, t))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      title={t('subscriber.visp.changeTitle', { login: subscriber.login })}
      onCancel={onClose}
      onOk={() => void submit()}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      confirmLoading={saving}
      okButtonProps={{ disabled: !vispId || vispId === subscriber.visp?.id }}
      destroyOnHidden
      afterOpenChange={(visible) => {
        if (!visible) return
        setError(null)
        setVispId(subscriber.visp?.id)
      }}
    >
      {error ? <Alert type="error" showIcon style={{ marginBottom: 16 }} message={error} /> : null}
      <Typography.Paragraph type="secondary">{t('subscriber.visp.changeHint')}</Typography.Paragraph>
      <Form layout="vertical" disabled={saving}>
        <Form.Item label={t('subscriber.visp.label')}>
          <VispSelect assignableOnly excludePooled value={vispId} onChange={setVispId} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
